import config, { Country, Language } from 'config';
import ReactPixel from 'react-facebook-pixel';
import { BumpedItemsExposure, ProductType } from 'types/product.types';
import { resultTypeCounts } from 'utils/searchResultSections';

import { STORAGE_ANALYTICS_DEBUG_KEY } from '../config/storage-keys';
import store from '../store';
import { getCountry, getLanguage } from '../utils/i18n';
import { bumpedCount, bumpedMarker } from './bumpedItems';

/** The viewer's own shop, so cached list bodies never mark the viewer's items as bumped. */
const viewerShopId = (): number | undefined => store.getState()?.userData?.user?.shop?.id;

const country = config.get('country');

export const gtmId = config.get(`analytics.gtmId`);
export const adsRevenueId = config.get(`analytics.adsRevenueId`);
export const segmentId = config.get(`analytics.segmentId`);
export const googleOptimizeId = config.get(`analytics.googleOptimizeId`);
export const gdprDomainKey = config.get('analytics.oneTrust.gdprDomainKey');
export const gaTrackingId = config.get(`analytics.gaId`);
export const tagManagerId = config.get(`analytics.tagManagerId`);
const fbqId = config.get('analytics.fbqId');

/** GA4 item_list_id per search surface — values match yaga-mobile so GA4 unions the streams. */
export const SEARCH_LIST_ID = {
  user_search: 'search_results',
  brand_browse: 'brand_results',
  category_browse: 'category_results',
} as const;

/**
 * Whether a result tile matched the buyer's typed words or is a related item suggested alongside
 * them. Item-scoped on view_item_list, event-scoped on select_item.
 *
 * Purely additive: `item_list_id` stays `search_results` for both, so every existing CTR and
 * click-rate metric keeps counting the two together with no query changes.
 */
export const RESULT_TYPE = {
  exact: 'exact',
  suggested: 'suggested',
} as const;

export type ResultType = (typeof RESULT_TYPE)[keyof typeof RESULT_TYPE];

/** GA4 item_list_id per shop list surface — shop_search_results matches yaga-mobile. */
export const SHOP_LIST_ID = {
  shop_search: 'shop_search_results',
  favourite_shops: 'favourite_shops',
  featured_shops: 'featured_shops',
} as const;

export type ShopListId = (typeof SHOP_LIST_ID)[keyof typeof SHOP_LIST_ID];

export type ShopListCue = 'shop_header' | 'more_items_tile';

/** source_module values for saved-search entries, carried onto view_item_list / select_item. */
export const SAVED_SEARCH_SOURCE_MODULE = 'saved_search';
export const SAVED_SEARCH_INAPP_SOURCE_MODULE = 'saved_search_inapp';

export type ShopListAnalyticsItem = {
  ownerId?: number;
  owner_id?: number;
  publishedProductsCount?: number;
  published_products_count?: number;
  feedbacks?: { total: number; avgRating: number | null } | null;
};

export type ShopFollowAnalytics = {
  item_list_id: ShopListId;
  item_id?: string;
  index: number;
  shop_item_count?: number;
  shop_review_count?: number;
  shop_rating?: number;
};

const shopListItemId = (shop: ShopListAnalyticsItem) => {
  const ownerId = shop?.ownerId ?? shop?.owner_id;

  return ownerId === undefined ? undefined : `shop_${ownerId}`;
};

const shopListAttributes = (shop: ShopListAnalyticsItem) => {
  const itemCount = shop?.publishedProductsCount ?? shop?.published_products_count;
  const reviewCount = shop?.feedbacks?.total;
  const rating = shop?.feedbacks?.avgRating;

  return {
    ...(Number.isInteger(itemCount) && { shop_item_count: itemCount }),
    ...(Number.isInteger(reviewCount) && { shop_review_count: reviewCount }),
    ...(typeof rating === 'number' && { shop_rating: rating }),
  };
};

const toShopListItem = (shop: ShopListAnalyticsItem, index: number, itemListId: ShopListId) => ({
  item_id: shopListItemId(shop),
  index,
  item_list_name: itemListId,
  ...shopListAttributes(shop),
});

export const shopFollowAnalytics = (shop: ShopListAnalyticsItem, itemListId: ShopListId, index: number): ShopFollowAnalytics => ({
  item_list_id: itemListId,
  item_id: shopListItemId(shop),
  index,
  ...shopListAttributes(shop),
});

export enum GTMEvents {
  ITEM_PUBLISHED = 'item_published',
  ITEM_EDIT_PUBLISHED = 'item_edit_published',
  OFFER_PRICE_RISE_PROMPT_ANSWERED = 'offer_price_rise_prompt_answered',
  // Shop Reviews tab (DEV-2612), only with feedback_role_tag_visible on. Params on both: shop_id
  // (reviewed shop), feedback_id. Same names and rules on mobile (CustomEvents).
  // - automatic_review_shown: an automatic review is at least half on screen in the Reviews tab,
  //   once per review per shop visit (tab switches do not re-count it).
  // - automatic_review_info_opened: each click on its info icon.
  AUTOMATIC_REVIEW_SHOWN = 'automatic_review_shown',
  AUTOMATIC_REVIEW_INFO_OPENED = 'automatic_review_info_opened',
  FIRST_LISTING = 'first_listing',
  PRICE_DROP_BADGE_VIEW = 'price_drop_badge_view',
  PRICE_DROP_BADGE_CLICK = 'price_drop_badge_click',
  PURCHASE = 'purchase',
  FIRST_PURCHASE = 'first_purchase',
  CHECKOUT_START = 'checkout_start',
  CHECKOUT_READY = 'checkout_ready',
  CHECKOUT_COMPLETE = 'checkout_complete',
  CHECKOUT_INTERACTION = 'checkout_interaction',
  CHECKOUT_ERROR = 'checkout_error',
  PAYMENT_CANCELLED_DISPLAYED = 'payment_cancelled_displayed',
  PAYMENT_CANCELLED_EXIT = 'payment_cancelled_exit',
  USER_SIGNUP = 'user_signup',
  USER_LOGIN = 'user_login',
  ONBOARDING_START = 'onboarding_start',
  ONBOARDING_COMPLETE = 'onboarding_complete',

  VIEW_ITEM_LIST = 'view_item_list',
  VIEW_ITEM = 'view_item',
  SELECT_ITEM = 'select_item',
  ADD_TO_CART = 'add_to_cart',
  BUTTON_CLICKED = 'button_clicked',
  ELEMENT_SEEN = 'element_seen',
  ELEMENT_CLICKED = 'element_clicked',
  SEARCH_KEYWORD = 'search_keyword',
  SEARCH = 'search',
  SEARCH_NO_RESULTS_VIEWED = 'search_no_results_viewed',
  NOTIFICATION_CLICKED = 'notification_clicked',
  UPDATE_CONSENT_PREFERENCES = 'update_consent_preferences',

  EXPERIMENT_VIEWED = 'experiment_viewed',

  CLICK_LIKE_BUTTON = 'click_like_button',
  SHOP_FOLLOWED = 'shop_followed',
  SHOP_UNFOLLOWED = 'shop_unfollowed',
  CLICK_BUY_BUTTON = 'click_buy_button',
  CLICK_MAKE_AN_OFFER_BUTTON = 'click_make_an_offer_button',
  CLICK_SELLER_SIDE_MAKE_AN_OFFER_BUTTON = 'click_seller_side_make_an_offer_button',
  CLICKED_SEARCH_RESULT_LISTING = 'clicked_search_result_listing',

  SAVED_SEARCH_CREATED = 'saved_search_created',
  SAVED_SEARCH_REMOVED = 'saved_search_removed',
  SAVED_SEARCH_OPENED = 'saved_search_opened',

  OTP_FORM_OPENED = 'otp_form_opened',
  OTP_FORM_CLOSED = 'otp_form_closed',
  OTP_SENT = 'otp_sent',
  OTP_CODE_ENTERED = 'otp_code_entered',
  OTP_VERIFIED = 'otp_verified',
  OTP_PURCHASE_GATE_EVALUATED = 'otp_purchase_gate_evaluated',

  ITEM_LISTING_START = 'item_listing_start',
  ITEM_LISTING_STEP_COMPLETED = 'item_listing_step_completed',
  ITEM_LISTING_EDIT_START = 'item_listing_edit_start',
  ITEM_LISTING_AI_PREFILL_START = 'item_listing_ai_prefill_start',
  ITEM_LISTING_AI_PREFILL_END = 'item_listing_ai_prefill_end',
  ITEM_LISTING_AI_PREFILL_LIMIT_REACHED = 'item_listing_ai_prefill_limit_reached',
  ITEM_LISTING_IMAGE_UPLOAD_AMOUNT = 'item_listing_image_upload_amount',
  SYSTEM_MESSAGE_PRESSED = 'system_message_pressed',
  NAME_VALIDATION_REJECTED = 'name_validation_rejected',
  INVITE_CTA_VIEWED = 'invite_cta_viewed',
  INVITE_LINK_SHARED = 'invite_link_shared',
  REFERRAL_CODE_COPIED = 'referral_code_copied',
  REFERRAL_FAQ_VIEWED = 'referral_faq_viewed',
  // Same name and params as the app's link tracking: click_link (identifier) and click_content (target).
  CLICK_LINK = 'click_link',
  // Same name and params as the app: address saves the server never sees (DEV-2506).
  ADDRESS_SAVE_FAILED = 'address_save_failed',
}

export enum GTMButtons {
  SAVE_LISTING = 'Save listing',
  ADD_PRODUCT = 'Add Product',
  BACK_BUTTON = 'Back button',
  TODAYSPICKS = "Today's picks",
  DOWNLOAD_APP_BUTTON = 'Download app',
  SIGNUP = 'Sign up',
  AI_TAG = 'AI tag',
  RESPOND_TO_PRICE_OFFER = 'Respond to price offer',
  OPEN_MODAL = 'Open modal',
  BACK_TO_TOP = 'Back to top',
}

export enum GTMElements {
  ERROR = 'Error on page',
  CLEAR_RECENTS = 'Clear recents',
  SEARCH_HISTORY_SUGGESTIONS = 'Search history suggestions',
  SEARCH_CATEGORY_SUGGESTIONS = 'Search category suggestions',
  SEARCH_BRAND_SUGGESTIONS = 'Search brand suggestions',
  SEARCH_VIEW_ALL = 'View all',
  SEARCH_KEYWORD = 'Search keyword',
  SEARCH_BAR = 'Search bar',
  SAVED_SEARCH_DISOCVERABILITY_POPUP = 'Saved Search discoverbaility popup',
  SAVED_SEARCH_DISOCVERABILITY_POPUP_LOGGED_IN = 'Saved Search discoverbaility popup for logged in user',
  APPSTORE_LINK = 'Apple Store link',
  PLAYSTORE_LINK = 'Google Store link',
  APP_STORES_BANNER = 'App Stores banner',
  CATEGORY_NAVBAR = 'Category navbar',
  RECOMMENDED_ITEM = 'Recommended item',
  HOME_MAIN_BANNER = 'Home main banner',
}

export enum GTMErrorContexts {
  PRODUCT_SAVE = 'product_save',
  PRODUCT_VALIDATION = 'product_save_field_validation',
  PRODUCT_IMAGE_ADD = 'product_image_add',
  PRODUCT_IMAGE_UPLOAD = 'product_image_upload',
}

export enum GTMLocations {
  ADD_LISTING_PAGE = 'Add listing page',
  SIDEBAR = 'Sidebar',
  FAQ = 'FAQ Page',
  PRODUCT_ADD_SUCCESS = 'Product Add Success page',
  MAIN_BUTTON = 'Main Button',
  SEARCH_BAR = 'Search Bar',
  SEARCH_DROPDOWN = 'Search Dropdown',
  NOTIFICATIONS_PAGE = 'Notification page',
  SEARCH = 'Search page',
  SELLING_LANDING_PAGE = 'Selling Landing Page',
  BUYING_LANDING_PAGE = 'Buying Landing Page',
  SELLING_LANDING_PAGE2 = 'Selling Landing Page 2',
  BUYING_LANDING_PAGE2 = 'Buying Landing Page 2',
  APP_DOWNLOAD_BANNER = 'Global App Download Banner',
  LISTING_PAGE = 'Listing page',
  SIGNUP_MODAL = 'Global Signup Login Modal',
  HOME_PAGE = 'Home page',
  PRODUCT_PAGE = 'Product page',
  CONVERSATION_MESSAGES = 'Conversation Messages',
  OFFER_PAGE = 'Offer page',
  BRAND = 'Brand page',
  CATEGORY = 'Category page',
}

// User properties interface for GTM
export interface GTMUserProperties {
  user_language?: Language;
  user_country?: Country;
  [key: string]: string | number | boolean | undefined;
}

export function cleanExtraParams(extraParams?: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(extraParams ?? {}).filter(([, value]) => value !== undefined && value !== '')) as Record<
    string,
    string
  >;
}

export const isAnalyticsDebugEnabled = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_ANALYTICS_DEBUG_KEY) === 'true';
  } catch {
    return false;
  }
};

const sendGtagEvent = (eventName: string, params: { [key: string]: any }) => {
  if (isAnalyticsDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.info(`%c[analytics] ${eventName}`, 'color: #0c66e4; font-weight: bold', params);
  }

  window.gtag('event', eventName, params);
};

// Google tag manager analytics
export const GTM = {
  pageview: (url, additionalParams?: { [key: string]: any }) => {
    const user = store?.getState()?.userData?.user || {};
    let userId;

    let userRole = 'anonymous';

    if (user?.id) {
      userId = `${user.id}`;
      userRole = user.role;
    }

    // Use user.language if available (for logged-in users), fallback to getLanguage()
    const userLanguage = user?.language || getLanguage();
    const userCountry = user?.country || getCountry();

    const gtagConfig = {
      page_path: url,
      page_title: url,
      cookie_prefix: `Yaga-${config.get('country')}-`,
      cookie_domain: config.get(`web.cookieDomain.${country}`),
      cookie_expires: 28 * 24 * 60 * 60,
      user_id: userId,
      user_role: userRole,
      ...additionalParams,
    };

    window.gtag('config', gtmId, gtagConfig);

    // Set user properties separately to ensure they're tracked
    GTM.updateUserProperties({
      user_language: userLanguage,
      user_country: userCountry,
    });
  },

  // Track current user properties to avoid overwriting them
  _currentUserProperties: {} as GTMUserProperties,

  updateUserProperties: (newProperties: Partial<GTMUserProperties>) => {
    try {
      // Merge with existing properties to avoid overwriting
      GTM._currentUserProperties = {
        ...GTM._currentUserProperties,
        ...newProperties,
      };

      window.gtag('set', 'user_properties', GTM._currentUserProperties);
    } catch (e) {
      console.error(e, 'Failed to update GTM user properties');
    }
  },

  logEvent: (eventName: GTMEvents, eventParams?: { [key: string]: any }) => {
    try {
      const { user, yagaUUID } = store?.getState()?.userData || {};
      const sharedProperties: {
        user_location: string;
        env_name: string;
        user_yaga_uuid: string;
        user_id?: string;
        user_role?: string;
      } = {
        env_name: config.get('country'),
        user_role: '',
        user_location: window.location.pathname,
        user_yaga_uuid: yagaUUID,
      };

      if (user?.id) {
        sharedProperties.user_id = `${user.id}`; // string cast by design
        sharedProperties.user_role = user.role;
      }

      sendGtagEvent(eventName, { ...eventParams, ...sharedProperties });
    } catch (e) {
      console.error(e, 'Failed to GTM logEvent');
    }
  },

  // Dedicated path for the checkout lifecycle snapshot events (DEV-2119): the snapshot alone is
  // ~22 params worst case against GA4's 25-param budget, so only env_name and user_id ride along
  // (24 of 25 in the maximal case — same headroom as mobile). logEvent's other shared props
  // (user_role, user_location, user_yaga_uuid) are user-scoped and derivable via a user_id join,
  // and would push the event over the cap.
  logCheckoutSnapshotEvent: (eventName: GTMEvents, eventParams?: { [key: string]: any }) => {
    try {
      const { user } = store?.getState()?.userData || {};
      const sharedProperties: {
        env_name: string;
        user_id?: string;
      } = {
        env_name: config.get('country'),
      };

      if (user?.id) {
        sharedProperties.user_id = `${user.id}`; // string cast by design
      }

      sendGtagEvent(eventName, { ...eventParams, ...sharedProperties });
    } catch (e) {
      console.error(e, 'Failed to GTM logCheckoutSnapshotEvent');
    }
  },

  /**
   * `resultTypeFor` marks each item exact vs suggested. A page can mix both, so the marker is
   * item-scoped; `exact_count` / `suggested_count` carry the same split at event level, where a
   * one-row-per-event warehouse table can read it without unnesting items.
   *
   * Omitted entirely when the arm doesn't distinguish the two, keeping the event byte-identical.
   */
  viewItemList: (
    products,
    itemListId?: string,
    startIndex = 0,
    extraParams?: Record<string, string | undefined>,
    resultTypeFor?: (absoluteIndex: number) => ResultType | undefined,
  ) => {
    const items = products?.map((p, index) => {
      const absoluteIndex = startIndex + index;
      const resultType = resultTypeFor?.(absoluteIndex);

      return {
        id: p.id,
        item_id: p.slug,
        item_brand: p.brand,
        price: p.price ? +p.price : undefined,
        index: absoluteIndex,
        google_business_vertical: 'retail',
        ...(resultType && { result_type: resultType }),
        ...bumpedMarker(p, viewerShopId()),
      };
    });
    // Keyed off whether any item actually carried a marker, never off the callback being
    // supplied: `exact_count: 0` would assert the page had no matches, when the honest answer on
    // fuzzy-common and on arms that report nothing is that no claim was made.
    const counts = resultTypeCounts(items?.map((i) => i.result_type) ?? []) ?? {};

    GTM.logEvent(GTMEvents.VIEW_ITEM_LIST, {
      value: products?.reduce((sum, p) => sum + +p.price, 0),
      ...(itemListId && { item_list_id: itemListId }),
      ...cleanExtraParams(extraParams),
      ...counts,
      ...bumpedCount(items ?? []),
      items,
    });
  },

  selectItem: (product, itemListId: string, index: number, extraParams?: Record<string, string | undefined>, resultType?: ResultType) => {
    if (!product) {
      return;
    }

    // One item per click, so the marker is unambiguous at event level too — which is what the
    // slim warehouse table reads. No new event and no change to event counts.
    GTM.logEvent(GTMEvents.SELECT_ITEM, {
      item_list_id: itemListId,
      ...cleanExtraParams(extraParams),
      ...(resultType && { result_type: resultType }),
      ...bumpedMarker(product, viewerShopId()),
      items: [
        {
          id: product.id,
          item_id: product.slug,
          item_brand: product.brand,
          price: product.price ? +product.price : undefined,
          index,
          google_business_vertical: 'retail',
          ...(resultType && { result_type: resultType }),
          ...bumpedMarker(product, viewerShopId()),
        },
      ],
    });
  },

  viewShopList: (shops: ShopListAnalyticsItem[], itemListId: ShopListId, startIndex = 0) => {
    if (!shops?.length) {
      return;
    }

    GTM.logEvent(GTMEvents.VIEW_ITEM_LIST, {
      item_list_id: itemListId,
      items: shops.map((shop, index) => toShopListItem(shop, startIndex + index, itemListId)),
    });
  },

  selectShop: (shop: ShopListAnalyticsItem, itemListId: ShopListId, index: number, cue: ShopListCue) => {
    if (!shop) {
      return;
    }

    GTM.logEvent(GTMEvents.SELECT_ITEM, {
      item_list_id: itemListId,
      cue,
      items: [toShopListItem(shop, index, itemListId)],
    });
  },

  shopFollowToggled: (analytics: ShopFollowAnalytics, liked: boolean, loginRequired = false) => {
    GTM.logEvent(liked ? GTMEvents.SHOP_FOLLOWED : GTMEvents.SHOP_UNFOLLOWED, {
      ...analytics,
      source: 'shop_list',
      login_required: loginRequired,
    });
  },

  viewItem: (product: ProductType, additionalParams?: { [key: string]: any }) => {
    if (!product) {
      return;
    }

    GTM.logEvent(GTMEvents.VIEW_ITEM, {
      value: +product.price,
      ...bumpedMarker(product, viewerShopId()),
      items: [
        {
          id: product.id,
          google_business_vertical: 'retail',
          ...bumpedMarker(product, viewerShopId()),
        },
      ],
      ...additionalParams,
    });
  },

  addToCart: (product) => {
    if (!product) {
      return;
    }

    GTM.logEvent(GTMEvents.ADD_TO_CART, {
      value: +product.price,
      items: [
        {
          id: product.id,
          google_business_vertical: 'retail',
        },
      ],
    });
  },

  buttonClicked: (button: GTMButtons, buttonLocation?: GTMLocations, additionalParams?: { [key: string]: any }) => {
    GTM.logEvent(GTMEvents.BUTTON_CLICKED, {
      button_name: button,
      button_location: buttonLocation,
      ...additionalParams,
    });
  },

  elementSeen: (element: GTMElements, elementLocation: GTMLocations, additionalParams?: { [key: string]: any }) => {
    GTM.logEvent(GTMEvents.ELEMENT_SEEN, {
      element_name: element,
      element_location: elementLocation,
      ...additionalParams,
    });
  },

  elementClicked: (element: GTMElements, elementLocation: GTMLocations, additionalParams?: { [key: string]: any }) => {
    GTM.logEvent(GTMEvents.ELEMENT_CLICKED, {
      element_name: element,
      element_location: elementLocation,
      ...additionalParams,
    });
  },

  searchKeyword: (element: GTMElements, elementLocation: GTMLocations, additionalParams?: { [key: string]: any }) => {
    GTM.logEvent(GTMEvents.SEARCH_KEYWORD, {
      element_name: element,
      element_location: elementLocation,
      ...additionalParams,
    });
  },

  search: (
    type: string,
    params: {
      query?: string;
      brands?: number[];
      categories?: {
        id: number;
        parentId?: number;
      };
      suggestionType?: string;
    },
  ) => {
    GTM.logEvent(GTMEvents.SEARCH, {
      type,
      search_term: params.query,
      category: params.categories?.id,
      parent_category: params.categories?.parentId,
      brands: params.brands,
    });
  },

  searchNoResultsViewed: (params: { query?: string; searchGuid?: string; searchContext?: string }) => {
    GTM.logEvent(GTMEvents.SEARCH_NO_RESULTS_VIEWED, {
      query: params.query,
      search_guid: params.searchGuid,
      search_context: params.searchContext,
    });
  },

  clickLikeButton: (productId: number) => {
    GTM.logEvent(GTMEvents.CLICK_LIKE_BUTTON, { product_id: productId });
  },

  clickBuyButton: (productId: number) => {
    GTM.logEvent(GTMEvents.CLICK_BUY_BUTTON, { product_id: productId });
  },

  clickMakeAnOfferButton: (productId: number) => {
    GTM.logEvent(GTMEvents.CLICK_MAKE_AN_OFFER_BUTTON, { product_id: productId });
  },

  clickSellerMakeAnOfferButton: (productId: number) => {
    GTM.logEvent(GTMEvents.CLICK_SELLER_SIDE_MAKE_AN_OFFER_BUTTON, { product_id: productId });
  },

  notificationClicked: (element: string, elementLocation: GTMLocations, additionalParams?: { [key: string]: any }) => {
    GTM.logEvent(GTMEvents.NOTIFICATION_CLICKED, {
      element_name: element,
      element_location: elementLocation,
      ...additionalParams,
    });
  },

  /** Server-assigned bumped_items exposure, carried on list responses; fires once per call for bucketed users. */
  bumpedItemsViewed: (exposure?: BumpedItemsExposure | null) => {
    if (exposure?.inExperiment && exposure.experimentId && exposure.variationId) {
      GTM.experimentViewed({ experimentId: exposure.experimentId, variationId: exposure.variationId });
    }
  },

  experimentViewed: ({ experimentId, variationId }: { experimentId: string; variationId: string }) => {
    GTM.logEvent(GTMEvents.EXPERIMENT_VIEWED, {
      experiment_id: experimentId,
      variation_id: variationId,
    });
  },

  searchItemClicked: (rankInSearch: number) => {
    GTM.logEvent(GTMEvents.CLICKED_SEARCH_RESULT_LISTING, {
      rank_in_search: rankInSearch,
    });
  },
};

export const PIXEL = {
  ...ReactPixel,

  init: () => {
    // Facebook pixel tracking
    if (typeof window !== 'undefined' && !window.PIXEL_INITIALIZED) {
      if (!fbqId) {
        console.error('Missing PIXEL id');

        return;
      }

      ReactPixel.init(fbqId);
      window.PIXEL_INITIALIZED = true;
    }
  },

  logPageView: () => {
    ReactPixel.pageView();
  },
};
